import { faObjectGroup } from "@fortawesome/pro-regular-svg-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { Icon, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { Spinner } from "@/components/Spinner/Spinner";
import { TextArea } from "@/components/TextArea/TextArea";
import {
	useCreateMessageTemplateMutation,
	useGetMessageTemplatesQuery,
} from "@/pages/Private/redux/messageTemplate/messageTemplate.api";
import { useSearch } from "@/utils";
import { classNames } from "@/utils/classNames";

export interface UseMessageTemplatesOptions {
	onInsertTemplate: (content: string) => void;
	initialDropdownPosition?: "top" | "bottom";
	/**
	 * Portal target for the templates dropdown. The Lead Panel passes its panel
	 * element so a click in the dropdown counts as *inside* the panel's
	 * outside-click boundary; defaults to `document.body`.
	 */
	portalContainer?: HTMLElement | null;
}

/**
 * Self-contained hook for the "Insert saved template" and "Save as template"
 * UX. Used by the inbox reply flow and by the campaign message editors.
 *
 * `onInsertTemplate` is invoked with the selected template's content when the
 * user picks one from the dropdown. The caller decides what to do with it
 * (set inbox draft state, push into a TinyMCE editor, etc.).
 */
export const useMessageTemplates = ({
	onInsertTemplate,
	initialDropdownPosition = "top",
	portalContainer,
}: UseMessageTemplatesOptions) => {
	const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
	const [showCreateTemplateModal, setShowCreateTemplateModal] = useState<boolean>(false);
	const [templateName, setTemplateName] = useState<string>("");
	const [templateMessage, setTemplateMessage] = useState<string>("");

	const dropdownRef = useRef<HTMLDivElement>(null);
	const templateButtonRef = useRef<HTMLButtonElement>(null);
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
	const [dropdownPosition, setDropdownPosition] = useState<"top" | "bottom">(
		initialDropdownPosition
	);

	const {
		query: templateQuery,
		setSearchValue: setTemplateSearchValue,
		page: templatePage,
		limit: templateLimit,
		searchValue: templateSearch,
		setQuery: setTemplateQuery,
	} = useSearch();

	const [createMessageTemplate, { isLoading: isCreatingMessageTemplate }] =
		useCreateMessageTemplateMutation();

	const { data: messageTemplates, isFetching: isFetchingMessageTemplates } =
		useGetMessageTemplatesQuery({
			page: templatePage,
			limit: templateLimit,
			searchValue: templateQuery,
		});

	const updateDropdownPosition = useCallback(() => {
		if (!templateButtonRef.current) {
			return;
		}

		const rect = templateButtonRef.current.getBoundingClientRect();
		// `fixed`, not `absolute`: the coords below are viewport-based, and when the dropdown
		// portals into the Lead Panel (a positioned wrapper) `absolute` would re-anchor them.
		const dropdownPositionStyle: React.CSSProperties = { position: "fixed" };

		if (dropdownPosition === "top") {
			dropdownPositionStyle.bottom = window.innerHeight - rect.top + 8;
			dropdownPositionStyle.right = window.innerWidth - rect.right;
		} else {
			dropdownPositionStyle.top = rect.bottom + 8;
			dropdownPositionStyle.right = window.innerWidth - rect.right;

			if (dropdownPositionStyle.top > window.innerHeight - 100) {
				dropdownPositionStyle.top = window.innerHeight - 100;
			}
		}

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setDropdownStyle(dropdownPositionStyle);
	}, [dropdownPosition]);

	useEffect(() => {
		if (showTemplateModal) {
			updateDropdownPosition();
			window.addEventListener("resize", updateDropdownPosition);
		} else {
			window.removeEventListener("resize", updateDropdownPosition);
		}

		return () => {
			window.removeEventListener("resize", updateDropdownPosition);
		};
	}, [showTemplateModal, updateDropdownPosition]);

	const handleOpenTemplateModal = useCallback(() => {
		setShowTemplateModal((prev) => !prev);
	}, []);

	const handleSelectTemplate = useCallback(
		(templateId: number) => {
			const content =
				messageTemplates?.data?.find((template) => template.id === templateId)?.content ?? "";

			onInsertTemplate(content);
			setShowTemplateModal(false);
			setTemplateSearchValue("");
			setTemplateQuery("");
		},
		[messageTemplates, onInsertTemplate, setTemplateSearchValue, setTemplateQuery]
	);

	const handleSaveAsTemplate = useCallback(async (msg: string) => {
		setShowCreateTemplateModal(true);
		setTemplateMessage(msg);
	}, []);

	const handleSendCreatedTemplate = useCallback(async () => {
		await createMessageTemplate({
			name: templateName,
			content: templateMessage,
		}).unwrap();
		setShowCreateTemplateModal(false);
		setTemplateName("");
		setTemplateMessage("");
	}, [createMessageTemplate, templateName, templateMessage]);

	const renderTemplateButton = () => (
		<div className="w-fit">
			<Button
				ref={templateButtonRef}
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="mr-2" icon={faObjectGroup} />}
				size={ButtonSize.XS}
				title="Template"
				onClick={() => handleOpenTemplateModal()}
			/>
		</div>
	);

	const renderMessageTemplatesModal = () =>
		ReactDOM.createPortal(
			<div
				ref={dropdownRef}
				className={classNames(
					"w-[300px] bg-white border border-border-light h-fit max-h-[50vh] scrollbar-hide overflow-y-auto p-2 rounded-lg z-[10000]"
				)}
				style={dropdownStyle}
			>
				<InputField
					handleChange={(e) => setTemplateSearchValue(e.target.value)}
					iconInside={
						templateSearch !== templateQuery || isFetchingMessageTemplates ? (
							<Spinner color="#6b7280" />
						) : undefined
					}
					name="templateSearch"
					placeholder="Search for templates"
					value={templateSearch}
				/>
				{messageTemplates?.data?.length === 0 && (
					<div className="text-sm text-text-meta font-normal flex items-center justify-center h-full">
						No templates found
					</div>
				)}
				<div className="flex flex-col gap-2 mt-2 p-2">
					{messageTemplates?.data &&
						messageTemplates?.data?.length > 0 &&
						messageTemplates?.data.map((template) => (
							<div
								key={template.id}
								className="bg-brand-white flex flex-col gap-1 cursor-pointer rounded-lg hover:bg-brand-light/10 text-sm text-text-meta font-normal flex items-start justify-start h-full p-2 w-full"
								onClick={() => handleSelectTemplate(template.id as number)}
							>
								<div className="text-sm text-text-meta font-semibold">{template.name}</div>
								<div className="text-xs text-text-meta font-normal max-w-full truncate text-ellipsis overflow-hidden">
									{template.content}
								</div>
							</div>
						))}
				</div>
			</div>,
			portalContainer ?? document.body
		);

	const renderCreateTemplateModal = () => (
		<Modal
			disableSave={!templateName || !templateMessage}
			handleClose={() => {
				setShowCreateTemplateModal(false);
				setTemplateName("");
				setTemplateMessage("");
			}}
			handleSave={() => handleSendCreatedTemplate()}
			isLoading={isCreatingMessageTemplate}
			isOpened={showCreateTemplateModal}
			submitButtonText="Save Template"
			title="Create Message Template"
		>
			<InputField
				handleChange={(e) => setTemplateName(e.target.value)}
				name="templateName"
				placeholder="Enter template name"
				value={templateName}
			/>
			<TextArea
				className="!mb-0 grow mr-4 max-h-[192px]"
				handleChange={(e) => setTemplateMessage(e.target.value)}
				name="templateMessage"
				placeholder="Enter template message"
				showError={false}
				value={templateMessage}
			/>
		</Modal>
	);

	/**
	 * Convenience helper for callers that want the full template UX in one place:
	 * renders the trigger button plus the dropdown and the "save as template" modal.
	 */
	const renderTemplateUI = () => (
		<>
			{renderTemplateButton()}
			{showTemplateModal && renderMessageTemplatesModal()}
			{showCreateTemplateModal && renderCreateTemplateModal()}
		</>
	);

	return {
		showTemplateModal,
		showCreateTemplateModal,
		dropdownPosition,
		setDropdownPosition,
		handleOpenTemplateModal,
		handleSaveAsTemplate,
		renderTemplateButton,
		renderMessageTemplatesModal,
		renderCreateTemplateModal,
		renderTemplateUI,
	};
};
